import React, { useEffect, useState } from "react";
import { FileUploader } from "react-drag-drop-files";
import Button from "@mui/material/Button/Button";
import TextField from "@mui/material/TextField/TextField";
import LinearProgress, {
  linearProgressClasses,
} from "@mui/material/LinearProgress";
import {
  Alert,
  Box,
  CircularProgress,
  Snackbar,
  Stack,
  Typography,
} from "@mui/material";
import styled from "@emotion/styled";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import { auth } from "../../firebase/firebase";

const CustomDropArea = styled("div")({
  border: "2px dashed #ffffff",
  borderRadius: "15px",
  padding: "40px",
  backgroundColor: "#A0AD39",
  textAlign: "center",
  cursor: "pointer",
  "&:hover": {
    backgroundColor: "#c1cd66",
  },
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
});

const TeldioTextField = styled(({ placeholderColor, ...other }) => (
  <TextField {...other} />
))(({ theme, placeholderColor }) => ({
  "& .MuiInputLabel-root": {
    color: placeholderColor || "black", // Set label text color to black
  },
  "& .MuiInputLabel-shrink": {
    color: "black", // Set shrunk label text color to black
  },
  "& .MuiOutlinedInput-root": {
    "& fieldset": {
      borderColor: "transparent", // Set border color
      borderRadius: "15px",
      boxShadow: "0 4px 30px rgba(0, 0, 0, 0.1)",
    },
    "&:hover fieldset": {
      borderColor: "#A0AD39", // Set border color on hover
    },
    "&.Mui-focused fieldset": {
      borderColor: "#A0AD39", // Set border color when focused
    },
  },
  "& .MuiInputBase-input": {
    color: placeholderColor || "black", // Set input text color to black
    "&::placeholder": {
      color: placeholderColor || "darkgrey", // Set placeholder text color
      opacity: 0.5, // Set the opacity of the placeholder text
    },
  },
}));

const BigButton = styled(Button)(({ theme }) => ({
  width: "90%",
  height: "40px",
  margin: "20px",
  borderRadius: "50px",
  backgroundColor: "#A0AD39",
  "&:hover": {
    backgroundColor: "#c1cd66",
  },
  fontSize: "17px",
  color: "white",
}));

const CancelButton = styled(Button)(({ theme }) => ({
  color: "#686868",
  fontWeight: "bold",
  fontFamily: "fonts/Oxygen-Light, Segoe UI, Roboto",
  textTransform: "none",
  fontSize: "15px",
  marginTop: "20px",
}));

function LinearProgressWithLabel(props) {
  return (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      <Box sx={{ width: "100%", mr: 1 }}>
        <LinearProgress variant="determinate" {...props} />
      </Box>
      <Box sx={{ minWidth: 35 }}>
        <Typography variant="body2" color="text.secondary">{`${Math.round(
          props.value
        )}%`}</Typography>
      </Box>
    </Box>
  );
}

const BorderLinearProgress = styled(LinearProgressWithLabel)(() => ({
  height: 10,
  borderRadius: 5,
  [`&.${linearProgressClasses.colorPrimary}`]: {
    backgroundColor: "#e7ebca",
  },
  [`& .${linearProgressClasses.bar}`]: {
    borderRadius: 5,
    backgroundColor: "#A0AD39",
  },
}));

const Uploader = ({ emailFromParams }) => {
  const [file, setFile] = useState(null);
  const [email, setEmail] = useState("");
  const [ccEmail, setCcEmail] = useState("");
  const [fileDesc, setFileDesc] = useState("");
  const [uploadProgress, setUploadProgress] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadId, setUploadId] = useState(null);
  const [gettingUploadUrl, setGettingUploadUrl] = useState(false);

  const [message, setMessage] = useState("");
  const [emailMessage, setEmailMessage] = useState("");

  // The base URL for generating the presigned URL
  const FUNCTIONS_URL = process.env.REACT_APP_API_URL;
  // The URL to confirm the upload (your confirmUpload function)
  const CONFIRM_URL = process.env.REACT_APP_API_URL_CONFIRM;

  useEffect(() => {
    if (emailFromParams) {
      setEmailWithChecks(emailFromParams);
    }
  }, [emailFromParams]);

  const handleClose = (event, reason) => {
    // Only close if it's not a "clickaway"
    if (reason !== "clickaway") {
      setMessage("");
    }
  };

  function cancelProcess() {
    setMessage("Upload Cancelled");
    setFile(null);
    setUploadProgress(null);
    setUploading(false);
  }

  function checkEmail(userEmail) {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(userEmail);
  }

  function setEmailWithChecks(userEmail) {
    if (checkEmail(userEmail)) {
      setEmail(userEmail);
    }
  }

  const handleChange = (newFile) => {
    setMessage("");
    setEmailMessage("");
    setUploadProgress(null);
    setFile(newFile);
  };

  function openFileExplorer() {
    const input = document.createElement("input");
    input.type = "file";
    setUploadProgress(null);

    input.onchange = (e) => {
      const selectedFile = e.target.files[0];
      setMessage("");
      setEmailMessage("");
      setFile(selectedFile);
    };
    input.click();
  }

  async function uploadFile() {
    if (!email || !checkEmail(email)) {
      setEmailMessage("Please Enter A Valid Email");
      return;
    }

    if (!file) {
      setMessage("Please select a file first");
      return;
    }

    try {
      // Show the initial API call state
      setGettingUploadUrl(true);
      setMessage("Preparing upload...");

      const fileType =
        file.type || `application/${file.name.split(".").pop().toLowerCase()}`;

      // Get the current user's ID token for authentication
      const currentUser = auth.currentUser;
      if (!currentUser) {
        setMessage("Authentication error. Please log in again.");
        setGettingUploadUrl(false);
        return;
      }

      // Get the ID token
      const idToken = await currentUser.getIdToken();

      // Step 1: Get a presigned URL from the backend with authentication
      const urlResponse = await fetch(FUNCTIONS_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`, // Add the auth token
        },
        body: JSON.stringify({
          fileName: file.name,
          fileType: fileType,
          fileSize: file.size,
          email,
          ccEmail: ccEmail || undefined,
        }),
      });

      if (!urlResponse.ok) {
        const error = await urlResponse.json().catch(() => ({}));
        throw new Error(error.message || "Failed to get upload URL");
      }

      const {
        uploadURL,
        fileKey,
        uploadId: newUploadId,
      } = await urlResponse.json();

      setUploadId(newUploadId);

      // Update state to show we're starting the actual upload
      setGettingUploadUrl(false);
      setUploading(true);
      setMessage("Starting file upload...");

      // Step 2: Upload the file to S3 using the presigned URL
      const xhr = new XMLHttpRequest();
      xhr.upload.addEventListener("progress", (event) => {
        if (event.lengthComputable) {
          const percentage = Math.round((event.loaded / event.total) * 100);
          setUploadProgress(percentage);
        }
      });

      await new Promise((resolve, reject) => {
        xhr.open("PUT", uploadURL);
        xhr.setRequestHeader("Content-Type", file.type);

        xhr.onload = () => {
          if (xhr.status === 200) {
            resolve();
          } else {
            reject(new Error("Upload failed"));
          }
        };

        xhr.onerror = () => reject(new Error("Upload failed"));
        xhr.send(file);
      });

      // Get a fresh token for the confirmation step
      const confirmToken = await currentUser.getIdToken(true);

      // Step 3: Confirm the upload on the server
      const confirmResponse = await fetch(CONFIRM_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${confirmToken}`, // Add the auth token
        },
        body: JSON.stringify({
          fileKey,
          uploadId: newUploadId,
          email,
          ccEmail: ccEmail || undefined,
          fileDesc,
          fileName: file.name,
        }),
      });

      if (!confirmResponse.ok) {
        const errJson = await confirmResponse.json().catch(() => ({}));
        throw new Error(errJson.error || "Failed to confirm upload");
      }

      setMessage("Upload Successful");
      setFile(null);
      setUploadProgress(100);
    } catch (error) {
      console.error("Upload error:", error);
      setMessage(error.message || "Upload Failed, Please try Again");
      setUploadProgress(0);
    } finally {
      setUploading(false);
      setGettingUploadUrl(false);
    }
  }

  return (
    <>
      <div className="itemContainer">
        {!file ? (
          <div style={{ marginTop: "80px", marginBottom: "60px" }}>
            <FileUploader handleChange={handleChange} name="file">
              <CustomDropArea>
                <InsertDriveFileIcon
                  fontSize="large"
                  sx={{ color: "white", mb: 1 }}
                />
                <Typography variant="subtitle1" sx={{ color: "white" }}>
                  Drag &amp; Drop or Click to Upload
                </Typography>
              </CustomDropArea>
            </FileUploader>{" "}
          </div>
        ) : null}

        {!file && (
          <BigButton variant="contained" fullWidth onClick={openFileExplorer}>
            Add File
          </BigButton>
        )}

        {file && (
          <div>
            <Typography
              variant="subtitle1"
              sx={{
                textAlign: "center",
                maxWidth: "100%",
                wordWrap: "break-word",
                marginTop: "40px",
              }}
            >
              File to Upload:
            </Typography>
            <Typography
              variant="subtitle2"
              sx={{
                textAlign: "center",
                maxWidth: "100%",
                wordWrap: "break-word",
                marginTop: "5px",
              }}
            >
              {file.name}
            </Typography>
          </div>
        )}

        {file && (
          <div style={{ marginTop: "20px" }}>
            <TeldioTextField
              id="outlined-basic"
              label="Your Email Address*"
              error={!!emailMessage}
              fullWidth
              autoFocus={!emailFromParams}
              value={email}
              variant="outlined"
              onChange={(e) => setEmail(e.target.value)}
            />
            {emailMessage && (
              <Stack
                direction="row"
                gap={1}
                alignItems="center"
                justifyContent="left"
                sx={{
                  marginTop: "5px",
                  color: "#D32E2E",
                  fontFamily: "fonts/Oxygen-Light",
                }}
              >
                <ErrorOutlineIcon />
                <Typography variant="subtitle2">{emailMessage}</Typography>
              </Stack>
            )}

            <TeldioTextField
              id="outlined-basic"
              label="CC Email (optional)"
              variant="outlined"
              fullWidth
              onChange={(e) => setCcEmail(e.target.value)}
              sx={{ marginTop: 2 }}
            />

            <TeldioTextField
              id="outlined-basic"
              label="Description of File (optional)"
              variant="outlined"
              fullWidth
              autoFocus={emailFromParams}
              onChange={(e) => setFileDesc(e.target.value)}
              sx={{ marginTop: 2 }}
            />
          </div>
        )}

        {file && (gettingUploadUrl || uploading) && (
          <Stack
            spacing={2}
            alignItems="center"
            justifyContent="center"
            sx={{ mt: 3, width: "100%" }}
          >
            {gettingUploadUrl ? (
              <CircularProgress size={24} sx={{ color: "#A0AD39" }} />
            ) : (
              uploadProgress !== null && (
                <Box sx={{ width: "100%" }}>
                  <BorderLinearProgress
                    variant="determinate"
                    value={uploadProgress}
                  />
                </Box>
              )
            )}
          </Stack>
        )}

        {file && !uploading && !gettingUploadUrl && (
          <BigButton variant="contained" fullWidth onClick={uploadFile}>
            Upload To Teldio
          </BigButton>
        )}

        <Snackbar
          open={!!message}
          autoHideDuration={6000}
          onClose={handleClose}
          sx={{ marginTop: "50px" }}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Alert
            onClose={handleClose}
            severity={
              message === "Upload Successful"
                ? "success"
                : message === "Preparing upload..." ||
                  message === "Starting file upload..."
                ? "info"
                : "error"
            }
            variant="filled"
            sx={{ width: "100%" }}
          >
            {message}
          </Alert>
        </Snackbar>
      </div>

      {file && !uploading && !gettingUploadUrl && (
        <CancelButton variant="text" fullWidth onClick={cancelProcess}>
          Cancel
        </CancelButton>
      )}
    </>
  );
};

export default Uploader;
